import type { CVDocument } from "./model";

/**
 * Direcciones dentro del documento: `"resume"`, `"experience[1].details"`.
 *
 * Es el ancla que comparten tres cosas que tienen que coincidir exactamente:
 * la sugerencia que devuelve el backend, el bloque que se resalta en el
 * documento y el editor que se abre en el panel. Con rutas en texto, el backend
 * puede senalar un fragmento sin conocer la forma de los componentes.
 */

export type Ruta = string;

type Segmento = { clave: string; indice?: number };

const CACHE = new Map<Ruta, Segmento[]>();

export const parsearRuta = (ruta: Ruta): Segmento[] => {
  const cacheada = CACHE.get(ruta);
  if (cacheada) return cacheada;

  const segmentos: Segmento[] = [];
  for (const parte of ruta.split(".")) {
    const m = parte.match(/^([A-Za-z_][A-Za-z0-9_]*)(?:\[(\d+)\])?$/);
    if (!m || !m[1]) throw new Error(`Ruta inválida: ${ruta}`);
    segmentos.push(
      m[2] === undefined
        ? { clave: m[1] }
        : { clave: m[1], indice: Number(m[2]) },
    );
  }

  CACHE.set(ruta, segmentos);
  return segmentos;
};

/** El valor en esa ruta, o `undefined` si el camino no existe. */
export const leerRuta = (doc: CVDocument, ruta: Ruta): unknown => {
  let actual: unknown = doc;

  for (const { clave, indice } of parsearRuta(ruta)) {
    if (actual == null || typeof actual !== "object") return undefined;
    actual = (actual as Record<string, unknown>)[clave];
    if (indice !== undefined) {
      if (!Array.isArray(actual)) return undefined;
      actual = actual[indice];
    }
  }

  return actual;
};

/**
 * Copia del documento con `valor` puesto en `ruta`.
 *
 * Inmutable hasta el fondo: solo se clonan los nodos del camino, y el resto se
 * comparte. Asi React puede comparar por referencia y no repinta el documento
 * entero cada vez que se teclea una letra en una viñeta.
 */
export const escribirRuta = (
  doc: CVDocument,
  ruta: Ruta,
  valor: unknown,
): CVDocument => {
  const segmentos = parsearRuta(ruta);

  const aplicar = (nodo: unknown, i: number): unknown => {
    const segmento = segmentos[i];
    if (!segmento) return valor;

    const { clave, indice } = segmento;
    const ultimo = i === segmentos.length - 1;
    const objeto = { ...(nodo as Record<string, unknown>) };

    if (indice === undefined) {
      objeto[clave] = ultimo ? valor : aplicar(objeto[clave], i + 1);
      return objeto;
    }

    const lista = Array.isArray(objeto[clave]) ? [...(objeto[clave] as unknown[])] : [];
    if (indice < 0 || indice >= lista.length) {
      throw new Error(`Índice fuera de rango en la ruta: ${ruta}`);
    }
    lista[indice] = ultimo ? valor : aplicar(lista[indice], i + 1);
    objeto[clave] = lista;
    return objeto;
  };

  return aplicar(doc, 0) as CVDocument;
};

/**
 * La seccion a la que pertenece una ruta, para agrupar en el panel de cambios.
 *
 * Se deriva de la ruta en vez de guardarse aparte: dos fuentes para el mismo
 * dato acaban discrepando, y aqui la ruta ya lo dice todo.
 */
export const seccionDeRuta = (ruta: Ruta): string => {
  const raiz = parsearRuta(ruta)[0]?.clave ?? "";
  switch (raiz) {
    case "resume":
      return "perfil";
    case "experience":
      return "experiencia";
    case "education":
      return "educacion";
    case "skills":
    case "soft_skills":
    case "tools":
      return "competencias";
    case "languages":
      return "idiomas";
    case "others":
      return "otros";
    default:
      return "contacto";
  }
};
